/**
 * TriggerPanel — right rail of the visual builder.
 *
 * Renders one card per workflow-level trigger. For TIME / EVENT triggers we
 * keep the structured-form fields (mode/cron/event dropdown). For CONDITION
 * triggers we show a mini ConditionDropZone so the user can build the
 * predicate by dragging fields from the left rail.
 */

import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import type { TriggerSpec, WorkflowEventType } from '@mana/shared';
import { WORKFLOW_EVENT_TYPES } from '@mana/shared';
import { Button } from '../../../ui/Button.js';
import { ConditionRow } from './ConditionRow.js';
import type { VisualConditionRow, VisualTrigger } from './serialize.js';

export const TRIGGER_DROP_PREFIX = 'trig-zone:';
export function makeTriggerZoneId(triggerId: string): string {
  return `${TRIGGER_DROP_PREFIX}${triggerId}`;
}
export function parseTriggerZoneId(zoneId: string): string | null {
  if (!zoneId.startsWith(TRIGGER_DROP_PREFIX)) return null;
  return zoneId.slice(TRIGGER_DROP_PREFIX.length);
}

export interface TriggerPanelProps {
  triggers: readonly VisualTrigger[];
  steps: ReadonlyArray<{ key: string; name: string }>;
  readOnly: boolean;
  onAdd: (kind: TriggerSpec['type']) => void;
  onRemove: (id: string) => void;
  onPatch: (id: string, next: VisualTrigger) => void;
}

export function TriggerPanel({
  triggers,
  steps,
  readOnly,
  onAdd,
  onRemove,
  onPatch,
}: TriggerPanelProps) {
  return (
    <aside
      className="flex h-full flex-col gap-3 border-l border-slate-200 bg-white/90 p-3"
      aria-label="Workflow triggers"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-brand-navy-800">
          Triggers
        </h3>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" disabled={readOnly} onClick={() => onAdd('time')}>
            + Time
          </Button>
          <Button size="sm" variant="ghost" disabled={readOnly} onClick={() => onAdd('event')}>
            + Event
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={readOnly}
            onClick={() => onAdd('condition')}
          >
            + Condition
          </Button>
        </div>
      </div>

      <div className="-mx-1 flex-1 space-y-3 overflow-y-auto px-1">
        {triggers.length === 0 && (
          <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
            No triggers yet. Add one to schedule when this workflow fires.
          </p>
        )}
        {triggers.map((vt) => (
          <TriggerCard
            key={vt.id}
            visual={vt}
            steps={steps}
            readOnly={readOnly}
            onPatch={(next) => onPatch(vt.id, next)}
            onRemove={() => onRemove(vt.id)}
          />
        ))}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
function TriggerCard({
  visual,
  steps,
  readOnly,
  onPatch,
  onRemove,
}: {
  visual: VisualTrigger;
  steps: ReadonlyArray<{ key: string; name: string }>;
  readOnly: boolean;
  onPatch: (next: VisualTrigger) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-card">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-navy-700">
            {visual.spec.type === 'time'
              ? '⏱ Time'
              : visual.spec.type === 'event'
                ? '⚡ Event'
                : '🧪 Condition'}
          </span>
        </div>
        <button
          type="button"
          disabled={readOnly}
          onClick={onRemove}
          title="Remove trigger"
          className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ✕
        </button>
      </div>
      <div className="space-y-2 px-3 py-2 text-xs">
        {visual.spec.type === 'time' && (
          <TimeTriggerFields
            spec={visual.spec}
            readOnly={readOnly}
            onChange={(spec) => onPatch({ ...visual, spec })}
          />
        )}
        {visual.spec.type === 'event' && (
          <EventTriggerFields
            spec={visual.spec}
            readOnly={readOnly}
            onChange={(spec) => onPatch({ ...visual, spec })}
          />
        )}
        {visual.spec.type === 'condition' && (
          <ConditionTriggerFields
            visual={visual}
            readOnly={readOnly}
            onPatch={onPatch}
          />
        )}

        <EntryStepField
          value={visual.spec.entryStepKey}
          steps={steps}
          readOnly={readOnly}
          onChange={(entryStepKey) =>
            onPatch({
              ...visual,
              spec: { ...visual.spec, entryStepKey } as TriggerSpec,
            })
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TimeTriggerFields({
  spec,
  readOnly,
  onChange,
}: {
  spec: Extract<TriggerSpec, { type: 'time' }>;
  readOnly: boolean;
  onChange: (next: Extract<TriggerSpec, { type: 'time' }>) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <label>
        <span className="block text-[10px] uppercase tracking-wider text-slate-500">Mode</span>
        <select
          value={spec.mode}
          onChange={(e) => onChange({ ...spec, mode: e.target.value as 'cron' | 'relative_offset' })}
          disabled={readOnly}
          className={fieldInputCls}
        >
          <option value="cron">Cron</option>
          <option value="relative_offset">Offset from loan date</option>
        </select>
      </label>
      <label>
        <span className="block text-[10px] uppercase tracking-wider text-slate-500">Timezone</span>
        <input
          type="text"
          value={spec.timezone}
          onChange={(e) => onChange({ ...spec, timezone: e.target.value })}
          disabled={readOnly}
          className={fieldInputCls}
        />
      </label>
      {spec.mode === 'cron' && (
        <label className="col-span-2">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500">
            Cron expression
          </span>
          <input
            type="text"
            value={spec.cron ?? ''}
            onChange={(e) => onChange({ ...spec, cron: e.target.value })}
            placeholder="0 9 * * 1-5"
            disabled={readOnly}
            className={`${fieldInputCls} font-mono`}
          />
        </label>
      )}
      {spec.mode === 'relative_offset' && (
        <>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500">
              Anchor
            </span>
            <select
              value={spec.anchorField ?? 'fundingDate'}
              onChange={(e) => onChange({ ...spec, anchorField: e.target.value })}
              disabled={readOnly}
              className={fieldInputCls}
            >
              <option value="lockDate">Lock date</option>
              <option value="fundingDate">Funding date</option>
              <option value="closingDateTime">Closing date</option>
              <option value="estimatedClosingDate">Estimated closing</option>
            </select>
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500">
              Offset (days)
            </span>
            <input
              type="number"
              value={spec.offsetDays ?? 0}
              onChange={(e) =>
                onChange({ ...spec, offsetDays: parseInt(e.target.value, 10) || 0 })
              }
              disabled={readOnly}
              className={fieldInputCls}
            />
          </label>
        </>
      )}
    </div>
  );
}

function EventTriggerFields({
  spec,
  readOnly,
  onChange,
}: {
  spec: Extract<TriggerSpec, { type: 'event' }>;
  readOnly: boolean;
  onChange: (next: Extract<TriggerSpec, { type: 'event' }>) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2">
      <label>
        <span className="block text-[10px] uppercase tracking-wider text-slate-500">Event</span>
        <select
          value={spec.event}
          onChange={(e) => onChange({ ...spec, event: e.target.value as WorkflowEventType })}
          disabled={readOnly}
          className={fieldInputCls}
        >
          {WORKFLOW_EVENT_TYPES.map((evt) => (
            <option key={evt} value={evt}>
              {evt}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="block text-[10px] uppercase tracking-wider text-slate-500">
          Match value (optional)
        </span>
        <input
          type="text"
          value={spec.matchValue ?? ''}
          onChange={(e) => onChange({ ...spec, matchValue: e.target.value || undefined })}
          disabled={readOnly}
          className={fieldInputCls}
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ConditionTriggerFields({
  visual,
  readOnly,
  onPatch,
}: {
  visual: VisualTrigger;
  readOnly: boolean;
  onPatch: (next: VisualTrigger) => void;
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: makeTriggerZoneId(visual.id),
    data: { kind: 'trig-zone', triggerId: visual.id },
  });

  const rows = visual.predicateRows ?? [];
  const logic = visual.predicateLogic ?? 'and';

  if (visual.predicateUnsupported) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
        Predicate has a nested boolean group — edit it in the Structured form
        tab.
      </div>
    );
  }

  const patchRow = (rowId: string, next: VisualConditionRow) => {
    onPatch({
      ...visual,
      predicateRows: rows.map((r) => (r.id === rowId ? next : r)),
    });
  };
  const removeRow = (rowId: string) => {
    onPatch({
      ...visual,
      predicateRows: rows.filter((r) => r.id !== rowId),
    });
  };

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'space-y-2 rounded-lg border-2 border-dashed p-2 transition-colors',
        isOver
          ? 'border-brand-gold-500 bg-brand-gold-50/60'
          : rows.length === 0
            ? 'border-slate-300 bg-slate-50'
            : 'border-transparent bg-transparent',
      )}
    >
      {rows.length > 1 && (
        <div className="flex items-center gap-2 text-[10px]">
          <span className="font-medium text-slate-500">Combine with</span>
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
            {(['and', 'or'] as const).map((op) => (
              <button
                key={op}
                type="button"
                disabled={readOnly}
                onClick={() => onPatch({ ...visual, predicateLogic: op })}
                className={clsx(
                  'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors',
                  logic === op
                    ? 'bg-brand-navy-700 text-white'
                    : 'text-slate-500 hover:bg-slate-50',
                )}
              >
                {op}
              </button>
            ))}
          </div>
        </div>
      )}
      {rows.map((row) => (
        <ConditionRow
          key={row.id}
          row={row}
          readOnly={readOnly}
          onChange={(next) => patchRow(row.id, next)}
          onRemove={() => removeRow(row.id)}
        />
      ))}
      {rows.length === 0 && (
        <p className="px-1 py-2 text-center text-[11px] text-slate-500">
          Drag a field here to build the trigger predicate.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function EntryStepField({
  value,
  steps,
  readOnly,
  onChange,
}: {
  value: string | undefined;
  steps: ReadonlyArray<{ key: string; name: string }>;
  readOnly: boolean;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500">
        Entry step
      </span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={readOnly}
        className={fieldInputCls}
      >
        <option value="">(first step)</option>
        {steps.map((s) => (
          <option key={s.key} value={s.key}>
            {s.name || s.key}
          </option>
        ))}
      </select>
    </label>
  );
}

const fieldInputCls =
  'mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-50';
